import { useNavigate } from 'react-router-dom';
import { Bell, HelpCircle, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import ProfileDropdown from './ProfileDropdown';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const relativeTime = (value) => {
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const TopBar = ({ title = 'Command Center', subtitle = 'Live Monitoring', sidebarOffset = 244 }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications?unread_only=true&limit=50`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications?limit=10`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        setNotifications(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (notificationsOpen) fetchNotifications();
  }, [notificationsOpen]);

  const markAsRead = async (notification) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notification.id}/read`, {
        method: 'POST',
        headers: authHeaders(),
      });
      setNotifications((prev) => prev.map((item) => (
        item.id === notification.id ? { ...item, read: true } : item
      )));
      setUnreadCount((count) => Math.max(0, count - (notification.read ? 0 : 1)));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
    const destination = notification.action_url || (notification.opportunity_id ? `/missions/${notification.opportunity_id}` : '');
    if (destination) navigate(destination);
    setNotificationsOpen(false);
  };

  const markAllRead = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (response.ok) {
        setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications read:', error);
    }
  };

  return (
    <header
      className="bsiq-product-topbar fixed top-0 right-0 z-50 h-16 flex justify-between items-center gap-4 px-4 sm:px-6 xl:px-8 transition-all duration-200"
      style={{
        left: `${sidebarOffset}px`,
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #E5E7EB',
        fontFamily: "'Public Sans', sans-serif",
      }}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="truncate text-[18px] font-bold tracking-[-0.02em] sm:text-[20px]" style={{ color: '#0D1F3C' }}>{title}</span>
        <span className="hidden h-4 w-[1px] sm:block" style={{ backgroundColor: '#E5E7EB' }}></span>
        <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 lg:block">{subtitle}</span>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <button
          type="button"
          onClick={() => navigate('/missions')}
          className="hidden min-h-10 w-64 items-center gap-2 rounded-xl border border-slate-200 bg-[#F8F9FB] px-3 text-left text-sm text-slate-500 transition-colors hover:border-slate-300 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0D1F3C] xl:flex"
          aria-label="Search workspace"
        >
          <Search className="h-4 w-4" />
          <span className="flex-1">Search workspace</span>
          <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-400">⌘ K</kbd>
        </button>
        <div className="flex gap-1">
          <div className="relative">
            <button
              onClick={() => setNotificationsOpen((open) => !open)}
              aria-label="Open notifications"
              aria-expanded={notificationsOpen}
              className="p-2 rounded-md transition-colors relative"
              style={{ color: '#6B7280' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F1F5F9')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              data-testid="topbar-notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full border text-[10px] font-black leading-[16px] text-white text-center" style={{ backgroundColor: '#EF4444', borderColor: '#FFFFFF' }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {notificationsOpen && (
              <div className="absolute right-0 top-12 w-80 rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden" data-testid="topbar-notifications-dropdown">
                <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
                  <span className="text-sm font-black" style={{ color: '#0D1F3C' }}>Notifications</span>
                  <button onClick={markAllRead} className="text-[11px] font-bold text-[#0D1F3C] hover:underline">Mark all read</button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-slate-500">No notifications yet</div>
                  ) : notifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => markAsRead(notification)}
                      className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex gap-2">
                        {!notification.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#EF4444] shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-sm font-bold truncate" style={{ color: '#0D1F3C' }}>
                            {notification.decided_by && notification.opportunity_title
                              ? `${notification.decided_by} made a GO decision on ${notification.opportunity_title}`
                              : notification.title || notification.message || 'Notification'}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">{notification.agency || 'BidsignalIQ'} — {relativeTime(notification.created_at)}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <button onClick={() => { setNotificationsOpen(false); navigate('/notifications'); }} className="w-full py-2.5 text-xs font-bold text-[#0D1F3C] hover:bg-slate-50">
                  View all notifications
                </button>
              </div>
            )}
          </div>
          <button
            aria-label="Open help"
            className="p-2 rounded-md transition-colors"
            style={{ color: '#6B7280' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F1F5F9')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            data-testid="topbar-help-btn"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="pl-2 sm:pl-4" style={{ borderLeft: '1px solid #E5E7EB' }}>
          <ProfileDropdown />
        </div>
      </div>
    </header>
  );
};

export default TopBar;
